import { Icons, Box } from "@sembark-travel/ui/base"
import {
	CopyToClipboardButton,
	CopyToClipboard,
} from "@sembark-travel/ui/copy-to-clipboard"
import { useMemo } from "react"
import { logError } from "@sembark-travel/logging"
import { Dialog, useDialog } from "@sembark-travel/ui/dialog"

export function JSONPreview({
	data,
	...styleProps
}: { data?: Record<string, unknown> } & Pick<
	React.ComponentProps<typeof Box>,
	"bgColor" | "color" | "borderColor"
>) {
	const stringified = useMemo(() => {
		try {
			return JSON.stringify(data || {}, undefined, 2)
				.replace(/\\n/g, "\n")
				.replace(/\\"/g, '"')
		} catch (e) {
			logError(e)
			return undefined
		}
	}, [data])
	return (
		<Box position="relative">
			{stringified ? (
				<Box position="absolute" top="0" right="0" padding="2">
					<CopyToClipboard>
						{({ copyTextToClipboard }) => (
							<CopyToClipboardButton
								size="sm"
								level="tertiary"
								onClick={() => copyTextToClipboard(stringified)}
							>
								<Icons.ClipboardCopy />
							</CopyToClipboardButton>
						)}
					</CopyToClipboard>
				</Box>
			) : null}
			<Box
				as="pre"
				maxWidth="full"
				overflow="auto"
				padding="4"
				rounded="md"
				bgColor="inset"
				borderWidth="1"
				fontFamily="mono"
				style={{ maxHeight: "250px" }}
				{...styleProps}
			>
				{stringified || "Missing Data"}
			</Box>
		</Box>
	)
}

export function JSONPreviewInDialog({
	children,
	title,
	...props
}: React.ComponentProps<typeof JSONPreview> & {
	children: (props: { preview: () => void }) => React.ReactNode
	title: string
}) {
	const [isOpen, openDialog, closeDialog] = useDialog()
	return (
		<>
			{children({ preview: openDialog })}
			<Dialog open={isOpen} onClose={closeDialog} title={title}>
				<Dialog.Body>
					<JSONPreview {...props} />
				</Dialog.Body>
			</Dialog>
		</>
	)
}
